import {ChangeDetectionStrategy, Component, Input} from '@angular/core';

@Component({
  selector: 'app-initials-avatar',
  templateUrl: './initials-avatar.component.html',
  styleUrls: ['./initials-avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class InitialsAvatarComponent {
  @Input() size = 48;

  initials = '--';
  backgroundColor = '';
  textColor = '';
  ariaLabel = 'User initials';

  constructor() {
    this.setColors(this.initials);
  }

  @Input() set text(value: string | null | undefined) {
    const text = value?.trim() ?? '';

    this.initials = this.getInitials(text);
    this.setColors(this.initials);
    this.ariaLabel = text ? `${text} initials` : 'User initials';
  }

  get fontSize(): number {
    return Math.max(10, Math.round(this.size * 0.44));
  }

  private getInitials(text: string): string {
    const words = text.split(/\s+/).filter(Boolean);

    if (words.length === 0) {
      return '--';
    }

    if (words.length === 1) {
      return Array.from(words[0]).slice(0, 2).join('').toUpperCase();
    }

    return words
      .slice(0, 2)
      .map((word) => Array.from(word)[0])
      .join('')
      .toUpperCase();
  }

  private setColors(initials: string): void {
    const colorKey = this.getColorKey(initials);
    const hue = colorKey % 360;
    const saturation = 54 + Math.floor(colorKey / 360) % 18;
    const lightness = 68 + Math.floor(colorKey / 6480) % 10;

    this.backgroundColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    this.textColor = this.getContrastColor(hue, saturation, lightness);
  }

  private getColorKey(initials: string): number {
    return Array.from(initials)
      .reduce((acc, char, index) => acc + (char.codePointAt(0) ?? 0) * Math.pow(131, index), 0);
  }

  private getContrastColor(hue: number, saturation: number, lightness: number): string {
    const [red, green, blue] = this.hslToRgb(hue, saturation, lightness)
      .map((channel) => {
        const value = channel / 255;
        return value <= 0.03928
          ? value / 12.92
          : Math.pow((value + 0.055) / 1.055, 2.4);
      });
    const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
    const blackContrast = (luminance + 0.05) / 0.05;
    const whiteContrast = 1.05 / (luminance + 0.05);

    return blackContrast >= whiteContrast ? '#000' : '#fff';
  }

  private hslToRgb(hue: number, saturation: number, lightness: number): [number, number, number] {
    const normalizedSaturation = saturation / 100;
    const normalizedLightness = lightness / 100;
    const chroma = (1 - Math.abs(2 * normalizedLightness - 1)) * normalizedSaturation;
    const hueSection = hue / 60;
    const secondaryColor = chroma * (1 - Math.abs(hueSection % 2 - 1));
    const offset = normalizedLightness - chroma / 2;

    const [red, green, blue] =
      hueSection < 1 ? [chroma, secondaryColor, 0] :
      hueSection < 2 ? [secondaryColor, chroma, 0] :
      hueSection < 3 ? [0, chroma, secondaryColor] :
      hueSection < 4 ? [0, secondaryColor, chroma] :
      hueSection < 5 ? [secondaryColor, 0, chroma] :
      [chroma, 0, secondaryColor];

    return [
      Math.round((red + offset) * 255),
      Math.round((green + offset) * 255),
      Math.round((blue + offset) * 255)
    ];
  }
}
